import { motion, Variants } from 'framer-motion';
import { ReactNode } from 'react';
import { useSharedElement } from '../../contexts/SharedElementContext';
import { useReducedMotion } from '../../hooks/useReducedMotion';

interface SharedElementProps {
  id: string;
  className?: string;
  children?: ReactNode;
  type?: 'div' | 'img' | 'h1' | 'h2' | 'h3' | 'p' | 'span';
  onAnimationComplete?: () => void;
  variants?: Variants;
  [key: string]: any;
}

export function SharedElement({
  id,
  className = '',
  children,
  type = 'div',
  onAnimationComplete,
  variants,
  ...props
}: SharedElementProps) {
  const { sharedElementId } = useSharedElement();
  const prefersReducedMotion = useReducedMotion();
  const isActive = sharedElementId === id.split('-').pop();
  
  const MotionComponent = motion[type] as any;
  
  // If user prefers reduced motion, don't use layoutId for shared element transitions
  if (prefersReducedMotion) {
    return (
      <MotionComponent
        className={className}
        variants={variants}
        {...props}
      >
        {children}
      </MotionComponent>
    );
  }
  
  return (
    <MotionComponent
      layoutId={isActive ? id : undefined}
      className={className}
      onLayoutAnimationComplete={isActive ? onAnimationComplete : undefined}
      variants={variants}
      style={{
        // Add performance optimizations for shared elements
        ...(isActive ? { willChange: 'transform, opacity' } : {}),
        ...(props.style || {})
      }}
      transition={{
        type: 'spring',
        stiffness: 300,
        damping: 30,
        ...(props.transition || {})
      }}
      {...props}
    >
      {children}
    </MotionComponent>
  );
} 